import { ApplicationInsights } from "@microsoft/applicationinsights-web";

let appInsights: ApplicationInsights | null = null;

const DEBOUNCE_DELAY = 1000; // 1 second

// Track debounced events
const debouncedEvents = new Map<string, ReturnType<typeof setTimeout>>();

// Track pending requests for cancellation
const pendingRequests = new Map<string, AbortController>();

// Guard against registering global error handlers more than once
let globalHandlersRegistered = false;

// Version info loaded from /version.json at startup
interface VersionInfo {
  version: string;
  buildId: string;
  env: string;
  release: string;
  mmYY: string;
}

let versionInfo: VersionInfo = {
  version: "",
  buildId: "",
  env: "",
  release: "",
  mmYY: "",
};

// Expose version info for use in API interceptors
export const getAppVersion = (): VersionInfo => versionInfo;

// Load version.json once at startup — does not throw
const loadVersionInfo = async (): Promise<void> => {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`);
    if (res.ok) {
      const data = await res.json();
      versionInfo = {
        version: data.version ?? "",
        buildId: data.buildId ?? "",
        env: data.env ?? "",
        release: data.release ?? "",
        mmYY: data.mmYY ?? "",
      };
    }
  } catch {
    // Non-fatal — version stays as empty strings
  }
};


// Register window.onerror and window.onunhandledrejection once
const setupGlobalErrorHandlers = (): void => {
  if (globalHandlersRegistered) return;
  globalHandlersRegistered = true;

  window.onerror = (message, source, lineno, colno, error) => {
    try {
      const err = error instanceof Error ? error : new Error(String(message));
      trackException(err, "GLOBAL", {
        source,
        lineno,
        colno,
        appVersion: versionInfo.version,
        buildId: versionInfo.buildId,
        env: versionInfo.env,
      });
    } catch {
      // Never let telemetry crash the app
    }
    return false; // Let default browser error handling continue
  };

  window.onunhandledrejection = (event: PromiseRejectionEvent) => {
    try {
      const reason = event.reason;
      const err = reason instanceof Error ? reason : new Error(String(reason));
      trackException(err, "UNHANDLED_PROMISE", {
        appVersion: versionInfo.version,
        buildId: versionInfo.buildId,
        env: versionInfo.env,
      });
    } catch {
      // Never let telemetry crash the app
    }
  };
};

// Initialize Application Insights
export const initializeAppInsights = () => {
  try {
    const connectionString = import.meta.env
      .VITE_APPINSIGHTS_INSTRUMENTATIONKEY;
    if (!connectionString) {
      return false;
    }

    const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN;

    appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true,
        enableCorsCorrelation: false,
        enableRequestHeaderTracking: false,
        enableResponseHeaderTracking: false,
      },
    });

    appInsights.addTelemetryInitializer((envelope) => {
      const url = envelope.baseData?.url || "";
      if (auth0Domain && url.includes(auth0Domain)) {
        return false;
      }
      return true;
    });

    appInsights.loadAppInsights();
    appInsights.trackPageView();

    // Load version info and wire global error handlers — both non-blocking
    loadVersionInfo();
    setupGlobalErrorHandlers();

    return true;
  } catch {
    return false;
  }
};

// Check app version against backend — no-op if VITE_VERSION_CHECK_URL is not set
export const checkAppVersion = async (): Promise<void> => {
  try {
    const versionCheckUrl = import.meta.env.VITE_VERSION_CHECK_URL;
    if (!versionCheckUrl || !versionInfo.version) return;

    const res = await fetch(versionCheckUrl);
    if (!res.ok) return;

    const config: { minSupportedVersion?: string; forceUpdate?: boolean } = await res.json();
    if (!config.minSupportedVersion || !config.forceUpdate) return;

    // Simple semver comparison: split on "." and compare each segment numerically
    const current = versionInfo.version.split(".").map(Number);
    const minimum = config.minSupportedVersion.split(".").map(Number);
    const maxLen = Math.max(current.length, minimum.length);

    for (let i = 0; i < maxLen; i++) {
      const c = current[i] ?? 0;
      const m = minimum[i] ?? 0;
      if (c < m) {
        // Version is outdated and force update is required
        window.location.replace("/update-required");
        return;
      }
      if (c > m) break;
    }
  } catch {
    // Non-fatal — never interrupt the app for a version check failure
  }
};

// Helper function to cancel pending request
const _cancelPendingRequest = (requestKey: string) => {
  const controller = pendingRequests.get(requestKey);
  if (controller) {
    controller.abort();
    pendingRequests.delete(requestKey);
  }
};

// Track trace (for diagnostic logging)
export const trackTrace = (
  message: string,
  severityLevel?: number,
  properties?: Record<string, unknown>
) => {
  try {
    if (!appInsights) return;

    const traceName = `frontend_${message}`;
    appInsights.trackTrace({ message: traceName, severityLevel }, properties);

  } catch { /* ignore */ }
};

// Track exception
export const trackException = (
  error: Error,
  moduleName: string,
  properties?: Record<string, unknown>
) => {
  try {
    if (!appInsights) return;

    const isProd = import.meta.env.VITE_ENV === "production";
    const errorPrefix = `frontend_${moduleName.toUpperCase()}_EXCEPTION`;

    const errorMessage = isProd
      ? `${errorPrefix}: Something went wrong.`
      : `${errorPrefix}: ${error.message}`;

    const trackingError = new Error(errorMessage);
    trackingError.name = error.name;

    if (!isProd) {
      trackingError.stack = error.stack;
    }

    appInsights.trackException({
      exception: trackingError,
      properties: {
        ...properties,
        moduleName,
      },
    });

  } catch { /* ignore */ }
};

// Track metric
export const trackMetric = (
  name: string,
  value: number,
  properties?: Record<string, unknown>
) => {
  try {
    if (!appInsights) return;

    appInsights.trackMetric(
      { name: `frontend_${name}`, average: value },
      properties
    );
  } catch { /* ignore */ }
};

// Added MS distributed tracing headers to API requests
// These allow Azure to stitch frontend and backend traces together
export const getCorrelationHeaders = (): Record<string, string> => {
  if (!appInsights) return {};

  const context = appInsights.context as any;
  return {
    "x-ms-request-id": context.operation?.id || "",
    "x-ms-request-root-id": context.operation?.id || "",
  };
};

export const setUserContext = (userId: string, userRoles?: string[]) => {
  try {
    if (!appInsights) return;

    appInsights.setAuthenticatedUserContext(userId, userRoles?.join(","), true);
  } catch { /* ignore */ }
};

// Clear user context (e.g., on logout)
export const clearUserContext = () => {
  try {
    if (!appInsights) return;

    appInsights.clearAuthenticatedUserContext();
  } catch { /* ignore */ }
};

export const trackPageView = (name?: string, url?: string, options?: { debounce?: boolean }) => {
  try {
    if (!appInsights) return;

    const pageViewKey = name || url || "default";

    const existingTimeout = debouncedEvents.get(pageViewKey);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    if (options?.debounce !== false) {
      const timeoutId = setTimeout(() => {
        appInsights!.trackPageView({
          name: name ? `frontend_${name}` : undefined,
          uri: url,
        });
        debouncedEvents.delete(pageViewKey);
      }, DEBOUNCE_DELAY);

      debouncedEvents.set(pageViewKey, timeoutId);
    } else {
      appInsights.trackPageView({
        name: name ? `frontend_${name}` : undefined,
        uri: url,
      });
    }
  } catch {
  }
};

// Cleanup function to cancel pending requests and debounced events
export const cleanup = () => {
  try {
    debouncedEvents.forEach((timeout) => clearTimeout(timeout));
    debouncedEvents.clear();

    pendingRequests.forEach((controller) => controller.abort());
    pendingRequests.clear();
  } catch {
  }
};

export default {
  initializeAppInsights,
  checkAppVersion,
  getAppVersion,
  trackTrace,
  trackException,
  trackMetric,
  getCorrelationHeaders,
  setUserContext,
  clearUserContext,
  trackPageView,
  cleanup,
};
